/**
 * The general ledger behind every finance number in the demo: a MYOB-style
 * chart of accounts and per-month actuals for each account.
 *
 * Each month is a pure function of its `YYYY-MM` key, so any quarter can be
 * computed and every surface (finance page, dashboard, board, CEO report,
 * assistant) agrees to the cent. The current month is partial: it is scaled
 * by the share of the month elapsed, counted in whole days so figures are
 * stable for the day.
 *
 * Shape of the year (~$42M revenue): NDIS + aged care fees, Commonwealth and
 * three state governments' grants, Tjanpi art sales, donations and interest.
 * Labour runs ~68-71% of revenue and spikes over the 70% target in the wet
 * season (Jan–Mar: road closures, charter flights, agency staff), which also
 * pushes that quarter to a small deficit. Corporate wages sit ~11.5-12.3%.
 *
 * Payroll helper (for the People area, so payroll cost agrees with the P&L):
 *   ledgerWages(fromYm, toYm)   { wages, superannuation } summed over YYYY-MM months inclusive; the current
 *                               month is month-to-date and future months count as 0. wages = WAGE_ACCOUNTS
 *                               (Direct Wages + Program Management Wages + Wages & Allowances, i.e. the labour
 *                               cost ratio's numerator); superannuation = SUPER_ACCOUNTS (Superannuation +
 *                               Superannuation – Corporate). Workers compensation is in neither.
 */
import { Quarter, addMonths, now, ym, ymd } from '../../clock';
import { rng } from '../../rng';

export type AccountSection = 'Income' | 'Cost of Sales' | 'Operating Expenses';

export interface Account {
    code: string;
    name: string;
    section: AccountSection;
}

export const ACCOUNTS: Account[] = [
    { code: '4-1100', name: 'NDIS – Disability Supports', section: 'Income' },
    { code: '4-1200', name: 'Aged Care – Home Care & CHSP', section: 'Income' },
    { code: '4-1300', name: 'NDIS – Support Coordination', section: 'Income' },
    { code: '4-2000', name: 'Commonwealth Grants – NIAA', section: 'Income' },
    { code: '4-2100', name: 'Commonwealth Grants – DSS', section: 'Income' },
    { code: '4-2200', name: 'NT Government Grants', section: 'Income' },
    { code: '4-2300', name: 'SA Government Grants', section: 'Income' },
    { code: '4-2400', name: 'WA Government Grants', section: 'Income' },
    { code: '4-3000', name: 'Tjanpi Sales – Galleries & Retail', section: 'Income' },
    { code: '4-4000', name: 'Donations & Philanthropy', section: 'Income' },
    { code: '4-9000', name: 'Interest Income', section: 'Income' },
    { code: '5-0150', name: 'Direct Wages', section: 'Cost of Sales' },
    { code: '5-5100', name: 'Program Management Wages', section: 'Cost of Sales' },
    { code: '5-0300', name: 'Superannuation', section: 'Cost of Sales' },
    { code: '5-0400', name: 'Workers Compensation', section: 'Cost of Sales' },
    { code: '5-0600', name: 'Tjanpi Artist Payments', section: 'Cost of Sales' },
    { code: '5-0700', name: 'Client & Program Consumables', section: 'Cost of Sales' },
    { code: '6-5100', name: 'Wages & Allowances', section: 'Operating Expenses' },
    { code: '6-5300', name: 'Superannuation – Corporate', section: 'Operating Expenses' },
    { code: '6-0200', name: 'Rent & Outgoings', section: 'Operating Expenses' },
    { code: '6-0510', name: 'Vehicle Leasing & Fleet', section: 'Operating Expenses' },
    { code: '6-0520', name: 'Fuel', section: 'Operating Expenses' },
    { code: '6-0600', name: 'Travel & Charter Flights', section: 'Operating Expenses' },
    { code: '6-0700', name: 'Insurance', section: 'Operating Expenses' },
    { code: '6-0800', name: 'IT & Software', section: 'Operating Expenses' },
    { code: '6-1100', name: 'Training & Development', section: 'Operating Expenses' },
    { code: '6-1200', name: 'Repairs & Maintenance', section: 'Operating Expenses' },
    { code: '6-1300', name: 'Depreciation', section: 'Operating Expenses' },
    { code: '6-1400', name: 'Professional Fees & Audit', section: 'Operating Expenses' },
    { code: '6-1500', name: 'Community Meetings & Governance', section: 'Operating Expenses' },
];

/** Accounts that make up the labour cost ratio, and the corporate subset. */
export const LABOUR_ACCOUNTS = ['5-0150', '5-5100', '6-5100'];
export const CORPORATE_ACCOUNT = '6-5100';
/** Payroll accounts read by ledgerWages(): gross wages, and superannuation on them. */
export const WAGE_ACCOUNTS = LABOUR_ACCOUNTS;
export const SUPER_ACCOUNTS = ['5-0300', '6-5300'];

/** Monthly revenue per income account at July 2025, before growth and seasonality. */
const INCOME_BASE: Record<string, number> = {
    '4-1100': 722000,
    '4-1200': 480000,
    '4-1300': 61000,
    '4-2000': 818000,
    '4-2100': 314000,
    '4-2200': 285000,
    '4-2300': 242000,
    '4-2400': 128000,
    '4-3000': 166000,
    '4-4000': 52000,
    '4-9000': 40000,
};

/** Seasonality by calendar month (index 1..12) for each kind of income. */
const SEASON = {
    fees: [0, 0.9, 0.96, 1.0, 0.99, 1.01, 1.0, 1.0, 1.01, 1.0, 1.02, 1.01, 0.94],
    grants: [0, 0.97, 0.98, 1.0, 0.99, 1.0, 1.06, 1.08, 0.98, 0.98, 0.99, 1.0, 0.97],
    tjanpi: [0, 0.62, 0.7, 0.88, 0.95, 1.05, 1.18, 1.22, 1.15, 1.32, 1.04, 1.06, 1.12],
    donations: [0, 0.6, 0.7, 0.8, 0.85, 0.9, 2.1, 0.8, 0.75, 0.8, 0.9, 1.05, 1.85],
    flat: [0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
};
const INCOME_SEASON: Record<string, number[]> = {
    '4-1100': SEASON.fees,
    '4-1200': SEASON.fees,
    '4-1300': SEASON.fees,
    '4-2000': SEASON.grants,
    '4-2100': SEASON.grants,
    '4-2200': SEASON.grants,
    '4-2300': SEASON.grants,
    '4-2400': SEASON.grants,
    '4-3000': SEASON.tjanpi,
    '4-4000': SEASON.donations,
    '4-9000': SEASON.flat,
};

/** Labour % of revenue: base plus a wet-season / Christmas bump. */
const LABOUR_BASE = 68.6;
const LABOUR_SEASON = [0, 2.8, 3.2, 2.0, 0.4, -0.2, -1.6, -0.6, -0.2, -0.5, 1.0, 1.3, 2.0];
/** Corporate wages % of revenue. */
const CORPORATE_BASE = 11.6;
const CORPORATE_SEASON = [0, 0.7, 0.9, 0.5, 0, -0.1, -0.6, 0.3, -0.1, -0.2, 0, 0.1, 0.4];
/** Non-wage operating expenses % of revenue (fleet, travel, rent, insurance...). */
const OTHER_OPEX_BASE = 13.3;
const OTHER_OPEX_SEASON = [0, 1.4, 1.6, 1.1, 0.1, 0, -0.3, 0.2, 0, -0.2, 0, 0.1, 0.3];

/** How the non-wage operating pool splits across accounts. */
const OTHER_OPEX_SPLIT: Record<string, number> = {
    '6-0200': 0.16,
    '6-0510': 0.18,
    '6-0520': 0.12,
    '6-0600': 0.15,
    '6-0700': 0.064,
    '6-0800': 0.08,
    '6-1100': 0.04,
    '6-1200': 0.07,
    '6-1300': 0.076,
    '6-1400': 0.033,
    '6-1500': 0.027,
};

/** Revenue growth per month (~3.6%/yr) from July 2025. */
const GROWTH = 0.003;
const GROWTH_ORIGIN = new Date(2025, 6, 1);

const r2 = (n: number): number => Math.round(n * 100) / 100;

function monthIndex(month: string): number {
    const [y, m] = month.split('-').map(Number);
    return (y - GROWTH_ORIGIN.getFullYear()) * 12 + (m - 1 - GROWTH_ORIGIN.getMonth());
}

/**
 * A full month of activity for every account. `jitter: false` gives the
 * smooth expectation the budgets are built from.
 */
export function fullMonth(month: string, jitter = true): Record<string, number> {
    const cal = Number(month.slice(5, 7));
    const growth = Math.pow(1 + GROWTH, monthIndex(month));
    const r = rng(`finance:ledger:${month}`);
    const j = (spread: number) => (jitter ? r.around(0, spread, -spread, spread, 4) : 0);

    const out: Record<string, number> = {};
    let revenue = 0;
    for (const [code, base] of Object.entries(INCOME_BASE)) {
        const v = r2(base * growth * INCOME_SEASON[code][cal] * (1 + j(0.035)));
        out[code] = v;
        revenue += v;
    }

    const labourPct = LABOUR_BASE + LABOUR_SEASON[cal] + j(0.5);
    const corpPct = CORPORATE_BASE + CORPORATE_SEASON[cal] + j(0.25);
    const otherPct = OTHER_OPEX_BASE + OTHER_OPEX_SEASON[cal] + j(0.35);

    const corporate = (revenue * corpPct) / 100;
    const direct = (revenue * labourPct) / 100 - corporate;
    out['5-0150'] = r2(direct * 0.86);
    out['5-5100'] = r2(direct * 0.14);
    out['5-0300'] = r2(direct * 0.115);
    out['5-0400'] = r2(direct * (0.026 + j(0.002)));
    out['5-0600'] = r2(out['4-3000'] * (0.55 + j(0.02)));
    out['5-0700'] = r2(revenue * (0.028 + j(0.003)));
    out['6-5100'] = r2(corporate);
    out['6-5300'] = r2(corporate * 0.115);
    const other = (revenue * otherPct) / 100;
    for (const [code, share] of Object.entries(OTHER_OPEX_SPLIT)) {
        out[code] = r2(other * share * (1 + j(0.06)));
    }
    return out;
}

/** Share of `month` that has happened: 1 for past months, 0 for future, whole days for the current one. */
export function monthFraction(month: string): number {
    const today = now();
    const current = ym(today);
    if (month < current) {
        return 1;
    }
    if (month > current) {
        return 0;
    }
    const days = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    return today.getDate() / days;
}

/** Actuals for one month (partial for the current month), or null for a future month. */
export function monthActuals(month: string): Record<string, number> | null {
    const f = monthFraction(month);
    if (f <= 0) {
        return null;
    }
    const full = fullMonth(month);
    if (f === 1) {
        return full;
    }
    const out: Record<string, number> = {};
    for (const [code, v] of Object.entries(full)) {
        out[code] = r2(v * f);
    }
    return out;
}

export interface MonthTotals {
    month: string;
    revenue: number;
    expenditure: number;
    cogs: number;
    labour: number;
    corporate: number;
}

export function totalsOf(month: string, accounts: Record<string, number>): MonthTotals {
    let revenue = 0;
    let cogs = 0;
    let opex = 0;
    for (const [code, v] of Object.entries(accounts)) {
        if (code.startsWith('4-')) {
            revenue += v;
        } else if (code.startsWith('5-')) {
            cogs += v;
        } else {
            opex += v;
        }
    }
    const labour = LABOUR_ACCOUNTS.reduce((s, c) => s + (accounts[c] ?? 0), 0);
    return {
        month,
        revenue: r2(revenue),
        expenditure: r2(cogs + opex),
        cogs: r2(cogs),
        labour: r2(labour),
        corporate: r2(accounts[CORPORATE_ACCOUNT] ?? 0),
    };
}

/** `monthly_trends` row for a month, or null if it hasn't started. */
export function monthTotals(month: string): MonthTotals | null {
    const a = monthActuals(month);
    return a ? totalsOf(month, a) : null;
}

/** First calendar month of the FY ending in `fy` (2027 → 2026-07). */
export function fyStartMonth(fy: number): string {
    return `${fy - 1}-07`;
}

/** `YYYY-MM` keys from `from` to `to` inclusive. */
export function monthRange(from: string, to: string): string[] {
    const out: string[] = [];
    for (let d = new Date(`${from}-01T00:00:00`); ym(d) <= to; d = addMonths(d, 1)) {
        out.push(ym(d));
    }
    return out;
}

/** The months a period covers that have started: a quarter's months, or the current FY to date. */
export function periodMonths(q: Quarter | null): string[] {
    const current = ym(now());
    if (q) {
        return q.months.filter(m => m <= current);
    }
    return monthRange(fyStartMonth(currentFy()), current);
}

/**
 * Months of the financial year to date as at the end of a period: for a
 * quarter, 1 July of that quarter's FY through the quarter's last started
 * month; for All Time (null), the current FY to date.
 */
export function fyToDateMonths(q: Quarter | null): string[] {
    if (!q) {
        return periodMonths(null);
    }
    const current = ym(now());
    const last = q.months[q.months.length - 1];
    const end = last < current ? last : current;
    return monthRange(fyStartMonth(q.fy), end);
}

/** Revenue for the financial year to date as at the end of a period (what "Revenue YTD" tiles show). */
export function revenueFyToDate(q: Quarter | null): number {
    const rows = fyToDateMonths(q)
        .map(monthTotals)
        .filter((m): m is MonthTotals => !!m);
    return r2(rows.reduce((s, m) => s + m.revenue, 0));
}

/** FY (by end year) we are in today. */
export function currentFy(): number {
    const d = now();
    return d.getMonth() >= 6 ? d.getFullYear() + 1 : d.getFullYear();
}

export interface Profitability {
    revenue_ytd: number;
    expenses_ytd: number;
    cogs_ytd: number;
    gross_profit_ytd: number;
    gross_profit_margin_percent: number;
    net_profit_ytd: number;
    operating_margin_percent: number;
    net_profit_margin_percent: number;
    labour_cost_ytd: number;
    labour_cost_ratio_percent: number;
    corporate_cost_ytd: number;
    corporate_cost_ratio_percent: number | null;
    fy_start: string;
    as_of: string;
}

/** MyobFinanceService::buildProfitabilityPayload over the period's months. */
export function profitabilityFor(q: Quarter | null): Profitability {
    const rows = periodMonths(q)
        .map(monthTotals)
        .filter((m): m is MonthTotals => !!m);
    const sum = (k: keyof Omit<MonthTotals, 'month'>) => r2(rows.reduce((s, m) => s + m[k], 0));
    const rev = sum('revenue');
    const exp = sum('expenditure');
    const cogs = sum('cogs');
    const labour = sum('labour');
    const corp = sum('corporate');
    const pct = (n: number) => (rev > 0 ? Math.round((n / rev) * 1000) / 10 : 0);

    const today = now();
    const tomorrow = ymd(new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1));
    let from: string;
    let to: string;
    if (q) {
        from = ymd(q.start);
        const next = ymd(new Date(q.end.getFullYear(), q.end.getMonth() + 1, 1));
        to = today.getTime() <= q.end.getTime() ? tomorrow : next;
    } else {
        from = `${fyStartMonth(currentFy())}-01`;
        to = ymd(today);
    }

    return {
        revenue_ytd: rev,
        expenses_ytd: exp,
        cogs_ytd: cogs,
        gross_profit_ytd: r2(rev - cogs),
        gross_profit_margin_percent: pct(rev - cogs),
        net_profit_ytd: r2(rev - exp),
        operating_margin_percent: pct(rev - exp),
        net_profit_margin_percent: pct(rev - exp),
        labour_cost_ytd: labour,
        labour_cost_ratio_percent: pct(labour),
        corporate_cost_ytd: corp,
        corporate_cost_ratio_percent: rev > 0 ? pct(corp) : null,
        fy_start: from,
        as_of: to,
    };
}

/** Per-account actuals summed over a list of months (only those that have started). */
export function accountTotals(months: string[]): Record<string, number> {
    const out: Record<string, number> = {};
    for (const m of months) {
        const a = monthActuals(m);
        if (!a) {
            continue;
        }
        for (const [code, v] of Object.entries(a)) {
            out[code] = (out[code] ?? 0) + v;
        }
    }
    for (const k of Object.keys(out)) {
        out[k] = r2(out[k]);
    }
    return out;
}

/**
 * Wages and superannuation expense from the ledger for `fromYm`..`toYm`
 * (YYYY-MM, inclusive). The current month is month-to-date (same scaling as
 * monthActuals); months that haven't started contribute 0.
 */
export function ledgerWages(fromYm: string, toYm: string): { wages: number; superannuation: number } {
    const totals = fromYm <= toYm ? accountTotals(monthRange(fromYm, toYm)) : {};
    const sum = (codes: string[]) => r2(codes.reduce((s, c) => s + (totals[c] ?? 0), 0));
    return { wages: sum(WAGE_ACCOUNTS), superannuation: sum(SUPER_ACCOUNTS) };
}

export { r2 };
